import { useState } from 'react';
import { Loader2, Search, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

function ClauseIcon({ pass }) {
  return pass ? (
    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 inline" />
  ) : (
    <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400 inline" />
  );
}

export function BookingTracePanel() {
  const [propertyId, setPropertyId] = useState('');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runTrace = async () => {
    if (!propertyId.trim()) {
      toast.error('Enter a property ID');
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.bookingTrace({
        property_id: propertyId.trim(),
        year,
        month,
      });
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const totals = results?.totals;
  const bookings = results?.bookings ?? [];
  const overlapping = bookings.filter((b) => b.clauses?.C_overlaps_month);
  const nonOverlapping = bookings.filter((b) => !b.clauses?.C_overlaps_month);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Per-Property Booking Trace</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Property ID
            </label>
            <input
              type="text"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              placeholder="uplisting_id or smoobu_id"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-48"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={runTrace} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run Trace
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-lg border p-3 bg-muted/30">
              <p className="font-medium">
                {results.property_name}{' '}
                <span className="text-xs text-muted-foreground font-mono">({results.property_id})</span>
              </p>
              <p className="text-sm text-muted-foreground">
                {results.platform} &middot; {results.month} &middot; today = {results.today}
              </p>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">All bookings (property)</p>
                <p className="text-xl font-bold tabular-nums">{totals.all_bookings_for_property}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Overlapping month</p>
                <p className="text-xl font-bold tabular-nums">{totals.bookings_overlapping_month}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Diagnostic included</p>
                <p className="text-xl font-bold tabular-nums text-green-600 dark:text-green-400">
                  {totals.diagnostic_included}
                </p>
              </div>
              <div className={`rounded-lg border p-3 ${totals.diagnostic_excluded > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Diagnostic excluded</p>
                <p className={`text-xl font-bold tabular-nums ${totals.diagnostic_excluded > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {totals.diagnostic_excluded}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Nights counted</p>
                <p className="text-xl font-bold tabular-nums">{totals.nights_counted}</p>
              </div>
              <div className={`rounded-lg border p-3 ${totals.nights_excluded > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Nights excluded</p>
                <p className={`text-xl font-bold tabular-nums ${totals.nights_excluded > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {totals.nights_excluded}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Available nights</p>
                <p className="text-xl font-bold tabular-nums">{totals.available_nights}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Occupancy (diagnostic)</p>
                <p className="text-xl font-bold tabular-nums">{totals.occupancy_pct}%</p>
              </div>
            </div>

            {/* Exclusion reasons breakdown */}
            {totals.exclusions_by_reason && Object.keys(totals.exclusions_by_reason).length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-3">
                <p className="text-sm font-medium mb-1">Exclusions by reason</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(totals.exclusions_by_reason).map(([reason, count]) => (
                    <Badge key={reason} variant="outline" className="text-xs text-red-700 dark:text-red-300">
                      {reason}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Filter clauses reference */}
            {results.filter_clauses_reference && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Filter clauses reference (code locations)
                </summary>
                <div className="mt-2 space-y-2 bg-muted/30 rounded-lg p-3">
                  <div>
                    <p className="font-medium">Diagnostic path</p>
                    <p className="text-muted-foreground">{results.filter_clauses_reference.diagnostic_query?.description}</p>
                    <p className="font-mono text-[10px] mt-1">{results.filter_clauses_reference.diagnostic_query?.code_location}</p>
                    <ul className="list-disc list-inside mt-1 text-muted-foreground">
                      {results.filter_clauses_reference.diagnostic_query?.python_exclusion?.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium">Dataset path</p>
                    <p className="text-muted-foreground">{results.filter_clauses_reference.dataset_query?.description}</p>
                    <p className="font-mono text-[10px] mt-1">{results.filter_clauses_reference.dataset_query?.code_location}</p>
                  </div>
                </div>
              </details>
            )}

            {/* Bookings table: overlapping month */}
            {overlapping.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Bookings overlapping {results.month} ({overlapping.length})
                </p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Booking ID</th>
                        <th className="text-left px-2 py-1.5 font-medium">Guest</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-out</th>
                        <th className="text-right px-2 py-1.5 font-medium">Nights</th>
                        <th className="text-left px-2 py-1.5 font-medium">Status</th>
                        <th className="text-left px-2 py-1.5 font-medium">Ingestion</th>
                        <th className="text-left px-2 py-1.5 font-medium">Channel</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="A: check_in exists">A</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="B: check_out exists">B</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="C: overlaps month">C</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="D: not cancelled">D</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="E: not quarantined">E</th>
                        <th className="text-center px-2 py-1.5 font-medium" title="F: not future">F</th>
                        <th className="text-center px-2 py-1.5 font-medium">Diag</th>
                        <th className="text-center px-2 py-1.5 font-medium">Dataset</th>
                        <th className="text-left px-2 py-1.5 font-medium">Exclusion reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overlapping.map((b, i) => (
                        <tr
                          key={b.booking_id || i}
                          className={`border-b last:border-0 ${!b.diagnostic_included ? 'bg-red-50/50 dark:bg-red-950/30' : ''}`}
                        >
                          <td className="px-2 py-1 font-mono text-[10px]">{b.booking_id || '-'}</td>
                          <td className="px-2 py-1">{b.guest_name || '-'}</td>
                          <td className="px-2 py-1 tabular-nums">{b.check_in}</td>
                          <td className="px-2 py-1 tabular-nums">{b.check_out}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{b.nights_in_month}</td>
                          <td className="px-2 py-1">{b.status}</td>
                          <td className="px-2 py-1">{b.ingestion_status}</td>
                          <td className="px-2 py-1">{b.channel || '-'}</td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.A_check_in_exists} /></td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.B_check_out_exists} /></td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.C_overlaps_month} /></td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.D_not_cancelled} /></td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.E_not_quarantined} /></td>
                          <td className="px-2 py-1 text-center"><ClauseIcon pass={b.clauses.F_not_future} /></td>
                          <td className="px-2 py-1 text-center">
                            {b.diagnostic_included ? (
                              <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">IN</Badge>
                            ) : (
                              <Badge variant="destructive" className="text-[10px]">OUT</Badge>
                            )}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {b.dataset_included ? (
                              <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">IN</Badge>
                            ) : (
                              <Badge variant="destructive" className="text-[10px]">OUT</Badge>
                            )}
                          </td>
                          <td className="px-2 py-1 text-[10px] text-red-600 dark:text-red-400 max-w-[300px]">
                            {b.diagnostic_exclusion_reasons?.join('; ') || b.dataset_extra_exclusion?.join('; ') || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Non-overlapping bookings (date issues) */}
            {nonOverlapping.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Non-overlapping bookings ({nonOverlapping.length}) - other months
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Booking ID</th>
                        <th className="text-left px-2 py-1.5 font-medium">Guest</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-out</th>
                        <th className="text-left px-2 py-1.5 font-medium">Status</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-in (raw)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nonOverlapping.map((b, i) => (
                        <tr key={b.booking_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono text-[10px]">{b.booking_id || '-'}</td>
                          <td className="px-2 py-1">{b.guest_name || '-'}</td>
                          <td className="px-2 py-1 tabular-nums">{b.check_in || '(empty)'}</td>
                          <td className="px-2 py-1 tabular-nums">{b.check_out || '(empty)'}</td>
                          <td className="px-2 py-1">{b.status}</td>
                          <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">{b.check_in_raw || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
